import { useCallback, useState } from 'react'
import { Definition, Word } from './useDoc'
import { get } from '../utils/http'

export type Vocab = {
  word: Word
  lang: string
  characterNum?: number
  definitions?: Definition[]
  numEncounters?: number
}

export const defineWord = async ({
  word,
  fromLang,
  toLang,
}: {
  fromLang: string
  toLang: string
  word: Word
}): Promise<Vocab | undefined> => {
  const definitions = (await get(
    `definitions/${word.clean}`,
    { params: { _: '_', fromLang, toLang } },
  )) as Definition[]

  if (!definitions?.length) return

  return {
    word,
    lang: fromLang,
    definitions,
  } as Vocab
}

const useVocab = ({
  vocab,
  setVocab,
  fromLang,
  toLang,
}: {
  fromLang: string
  toLang: string
  vocab?: Vocab[]
  setVocab: (
    callback: (
      v: Vocab[] | undefined,
    ) => Vocab[] | undefined,
  ) => void
}) => {
  const [vocabWord, setVocabWord] = useState<
    Vocab | undefined
  >()
  const [isLoading, setLoading] = useState(false)

  const add = useCallback(
    (v: Vocab | undefined) => {
      if (!v) return

      setVocab((list) => {
        if (
          (list ?? []).find(
            ({ word }) => word?.token === v.word?.token,
          )
        )
          return list
        return [v, ...(list ?? [])]
      })
    },
    [setVocab],
  )

  const remove = useCallback(
    (v: Vocab | undefined) => {
      if (!v) return

      setVocab((list) =>
        (list ?? []).filter(({ word }) => word !== v.word),
      )
    },
    [setVocab],
  )

  const define = useCallback(
    (word: Word) => {
      if (!word) return

      const existing = vocab?.find(
        ({ word: w }) => w.token === word.token,
      )

      if (existing) {
        setVocabWord(existing)
        return
      }

      setLoading(true)
      defineWord({ fromLang, toLang, word })
        .then((vw) => {
          setLoading(false)
          setVocabWord(vw)
        })
        .catch(() => {
          setLoading(false)
          setVocabWord(undefined)
        })
    },
    [vocab, fromLang, toLang],
  )

  return {
    add,
    remove,
    define,
    vocab,
    setVocab,
    setVocabWord,
    vocabWord,
    isLoading,
  }
}

export default useVocab
